import React, { useState, useEffect } from 'react';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { USER_PROFILE, USER_ROLE } from '../../services/auth';
import { useBranches } from '../../context/branches';
import { useDimensions, useVehicleTypes } from '../../context/vehicles';
import { downloadFile, downloadLocalFile } from '../../utils/fileDownload';
import { checkVehicleNumber, createVehicle, updateVehiclePartial } from '../../services/vehicles';
import Spinner from '../../components/ui/Spinner';
import { normalizeDateTime } from "../../utils/datetime";

const VEHICLE_NUMBER_REGEX = /^[A-Z]{2}\d{1,2}(?:[A-Z]{1,2})?\d{1,4}$/;

const VehicleForm = ({ vehicle, vehicleMode, onSave, onCancel }) => {

  const role = localStorage.getItem(USER_ROLE);
  const showBranches = (role === 'admin');
  const profile = JSON.parse(localStorage.getItem(USER_PROFILE));
  const ownVehicle = (vehicleMode === 'own');
  const isEditMode = Boolean(vehicle?.id);

  let branches = [], branch_loading = false, refreshBranches = null;
  if (showBranches) {
    const result = useBranches({ reload: false, params: { status: 'active', paginate: false } });
    branches = result.branches;
    branch_loading = result.loading;
    refreshBranches = result.refresh;
  }
  const { dimensions, loading: dimension_loading } = useDimensions({ reload: false });
  const { vehicleTypes, loading: vehicle_type_loading } = useVehicleTypes({ reload: false });

  // ---------- Phase state ----------
  // In create mode we start at phase 1 (vehicle number entry).
  // In edit mode we skip straight to phase 2 (full form).
  const [phase, setPhase] = useState(isEditMode ? 2 : 1);
  const [vehicleNumberInput, setVehicleNumberInput] = useState(
    (vehicle?.vehicle_number || '').replace(/\s+/g, '')
  );
  const [vehicleNumberError, setVehicleNumberError] = useState('');
  const [checkingNumber, setCheckingNumber] = useState(false);

  // ---------- Form state ----------
  const [errors, setErrors] = useState({});
  const [initialData, setInitialData] = useState(vehicle || {});
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    id: vehicle?.id || 0,
    vehicle_code: vehicle?.vehicle_code || '',
    vehicle_number: (vehicle?.vehicle_number || '').replace(/\s+/g, ''),
    vehicle_type: vehicle?.vehicle_type || '',
    vehicle_mode: vehicle?.vehicle_mode || vehicleMode,
    vehicle_type_id: vehicle?.vehicle_type_id || 0,
    dimension: vehicle?.dimension || '',
    dimension_id: vehicle?.dimension_id || 0,
    branch: vehicle?.branch || '',
    branch_id: showBranches ? (vehicle?.branch_id || 0) : profile.branch?.id,
    rc_number: vehicle?.rc_number || '',
    chassis_number: vehicle?.chassis_number || '',
    rc_doc: vehicle?.rc_doc || '',
    insurance_number: vehicle?.insurance_number || '',
    insurance_expiry: vehicle?.insurance_expiry || '',
    insurance_doc: vehicle?.insurance_doc || '',
    fc_date: vehicle?.fc_date || '',
    fc_doc: vehicle?.fc_doc || '',
    national_permit_date: vehicle?.national_permit_date || '',
    national_permit_doc: vehicle?.national_permit_doc || '',
    state_permit_date: vehicle?.state_permit_date || '',
    state_permit_doc: vehicle?.state_permit_doc || '',
    pollution_cert_date: vehicle?.pollution_cert_date || '',
    pollution_cert_doc: vehicle?.pollution_cert_doc || '',
    tax_receipt_date: vehicle?.tax_receipt_date || '',
    tax_receipt_doc: vehicle?.tax_receipt_doc || '',
    is_active: vehicle?.is_active ? 1 : 0,
  });
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (vehicle) {
      const vn = (vehicle?.vehicle_number || '').replace(/\s+/g, '');
      setForm(prev => ({ ...prev, ...vehicle, vehicle_number: vn }));
      setVehicleNumberInput(vn);
    }
  }, [vehicle]);

  // -------------------------------------------------------
  // Phase 1: vehicle number validation
  // -------------------------------------------------------
  const handleVehicleNumberChange = (e) => {
    const val = e.target.value.replace(/\s+/g, '').toUpperCase();
    setVehicleNumberInput(val);
    setVehicleNumberError('');
  };

  const handleNext = async () => {
    const normalized = vehicleNumberInput.trim();

    // Client-side format check
    if (!normalized) {
      setVehicleNumberError('Vehicle number is required.');
      return;
    }
    if (!VEHICLE_NUMBER_REGEX.test(normalized)) {
      setVehicleNumberError('Invalid format. Example: TN22AF5634');
      return;
    }

    // Server-side existence check
    setCheckingNumber(true);
    const [err, data] = await checkVehicleNumber(normalized, isEditMode ? vehicle.id : null);
    setCheckingNumber(false);

    if (err) {
      setVehicleNumberError('Could not verify vehicle number. Please try again.');
      return;
    }
    if (data?.exists) {
      setVehicleNumberError('This vehicle number is already registered.');
      return;
    }

    // All good — carry number into form and advance to phase 2
    setForm(prev => ({ ...prev, vehicle_number: normalized }));
    setVehicleNumberError('');
    setPhase(2);
  };

  // -------------------------------------------------------
  // Phase 2: rest of the form
  // -------------------------------------------------------
  const handleChange = (e) => {
    const { name, files } = e.target;
    let { value } = e.target;
    if (name === 'vehicle_number') {
      const normalizedVehicleNumber = (value || '').replace(/\s+/g, '').toUpperCase();
      setForm(prev => ({ ...prev, [name]: normalizedVehicleNumber }));
      return;
    }
    if (!files && typeof value === 'string') {
      value = value.toUpperCase();
    }
    setForm(prev => ({ ...prev, [name]: files ? files[0] : value }));
  };

  const updateVehicleType = (index) => {
    setForm(prev => ({ ...prev, vehicle_type: vehicleTypes[index], vehicle_type_id: vehicleTypes[index].id }));
  };

  const updateDimension = (index) => {
    setForm(prev => ({ ...prev, dimension: dimensions[index], dimension_id: dimensions[index].id }));
  };

  const updateBranch = (index) => {
    setForm(prev => ({ ...prev, branch: branches[index], branch_id: branches[index].id }));
  };

  const handleDownload = (e, type) => {
    e.preventDefault();
    const file = form[`${type}_doc`];
    if (file instanceof File) {
      downloadLocalFile(file);
    } else if (typeof file === 'string') {
      const filename = file.split('/').pop();
      downloadFile(file, filename);
    }
  };

  const validate = () => {
    const newErrors = {};
    const normalizedVehicleNumber = (form.vehicle_number || '').replace(/\s+/g, '');
    if (!normalizedVehicleNumber) newErrors.vehicle_number = 'Vehicle Number is required';
    else if (!VEHICLE_NUMBER_REGEX.test(normalizedVehicleNumber)) {
      newErrors.vehicle_number = 'Invalid vehicle number. Example: TN22AF5634';
    }
    return newErrors;
  };

  const getChangedFields = (initial, updated) => {
    const changed = {};
    const dt_keys = [
      'national_permit_date', 'insurance_expiry', 'fc_date',
      'state_permit_date', 'pollution_cert_date', 'tax_receipt_date',
    ];
    for (const key in updated) {
      if (Array.isArray(updated[key])) {
        if (JSON.stringify(initial[key]) !== JSON.stringify(updated[key])) {
          changed[key] = updated[key];
        }
      } else if (
        dt_keys.includes(key) &&
        normalizeDateTime(initial[key]) === normalizeDateTime(updated[key])
      ) {
        continue;
      } else if (updated[key] !== initial[key]) {
        changed[key] = updated[key];
      }
    }
    return changed;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationErrors({});
    const normalizedVehicleNumber = (form.vehicle_number || '').replace(/\s+/g, '');
    const payload = { ...form, vehicle_number: normalizedVehicleNumber };
    const errs = validate();
    if (Object.keys(errs).length) {
      setValidationErrors(errs);
      return;
    }
    let err, response;
    setLoading(true);
    if (payload.id) {
      const changedFields = getChangedFields(initialData, payload);
      if (Object.keys(changedFields).length === 0) {
        setLoading(false);
        return;
      }
      [err, response] = await updateVehiclePartial(payload.id, changedFields);
    } else {
      payload.is_active = 1;
      [err, response] = await createVehicle(payload);
    }
    setLoading(false);
    if (err) {
      setErrors(err);
    } else {
      onSave(response);
    }
  };

  const renderFileInput = (field, label, file) => (
    <div>
      <label className="block text-sm py-1">{label}</label>
      <div className="flex items-center gap-2">
        <label className="cursor-pointer bg-blue-600 text-white px-3 py-1.5 rounded text-sm hover:bg-blue-700 uppercase">
          Choose File
          <input
            type="file"
            name={`${field}_doc`}
            onChange={handleChange}
            className="hidden"
          />
        </label>
        {file && (
          <button
            onClick={(e) => handleDownload(e, field)}
            className="text-green-600 hover:text-green-800 p-2 rounded hover:bg-green-50"
            title="Download"
          >
            <ArrowDownTrayIcon className="w-5 h-5" />
          </button>
        )}
      </div>
      {file && (
        <span className="text-sm text-gray-600 truncate max-w-[150px]">{file.name}</span>
      )}
      {errors[`${field}_doc`] && <p className="text-red-500 text-sm mt-1">{errors[`${field}_doc`][0]}</p>}
    </div>
  );

  // -------------------------------------------------------
  // Phase 1 render: vehicle number + Next button
  // -------------------------------------------------------
  if (phase === 1) {
    return (
      <div className="bg-white p-6 rounded shadow max-w-sm mx-auto mt-6">
        <h2 className="text-base font-semibold text-gray-700 mb-4">Enter Vehicle Number</h2>
        <div className="flex flex-col gap-1 mb-5">
          <label className="text-sm text-gray-600">Vehicle Number</label>
          <input
            type="text"
            value={vehicleNumberInput}
            onChange={handleVehicleNumberChange}
            onKeyDown={(e) => e.key === 'Enter' && handleNext()}
            placeholder="E.g. TN22AF5634"
            className={`border rounded px-3 py-2 text-sm uppercase tracking-widest focus:outline-none focus:ring-1
              ${vehicleNumberError ? 'border-red-400 focus:ring-red-400' : 'border-gray-300 focus:ring-blue-400'}`}
            autoFocus
          />
          {vehicleNumberError && (
            <p className="text-red-500 text-xs mt-1">{vehicleNumberError}</p>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleNext}
            disabled={checkingNumber}
            className="px-5 py-2 rounded bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {checkingNumber && <Spinner size={14} color="#fff" inline />}
            {checkingNumber ? 'Checking...' : 'Next'}
          </button>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------
  // Phase 2 render: full form
  // -------------------------------------------------------
  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-6 bg-white p-6 rounded shadow [&_input[type='text']]:uppercase [&_textarea]:uppercase [&_select]:uppercase"
    >
      {/* Section: Vehicle Details */}
      <fieldset className="border border-gray-300 rounded p-4">
        <legend className="text-lg font-semibold">Vehicle Details</legend>
        <div className="grid md:grid-cols-4 gap-4 mt-2">

          {/* Vehicle Number — locked after phase 1, editable in edit mode inline */}
          <div>
            <label className="block text-sm">Vehicle Number</label>
            <div className="flex items-center gap-2">
              <input
                name="vehicle_number"
                value={form.vehicle_number}
                onChange={handleChange}
                readOnly={!isEditMode}
                className={`w-full border px-2 py-1 rounded ${!isEditMode ? 'bg-gray-50 text-gray-500 cursor-default' : ''}`}
              />
              {!isEditMode && (
                <button
                  type="button"
                  onClick={() => setPhase(1)}
                  className="text-xs text-blue-600 hover:underline whitespace-nowrap"
                >
                  Change
                </button>
              )}
            </div>
            {errors.vehicle_number && <p className="text-red-500 text-sm mt-1">{errors.vehicle_number[0]}</p>}
            {validationErrors.vehicle_number && (
              <p className="text-red-500 text-sm mt-1">{validationErrors.vehicle_number}</p>
            )}
          </div>

          {/* Vehicle Type */}
          <div>
            <label className="block text-sm">Vehicle Type</label>
            <select
              name="vehicle_type"
              value={form.vehicle_type?.name || ''}
              onChange={(e) => updateVehicleType(e.target.selectedIndex - 1)}
              className="w-full border px-2 py-1 rounded"
            >
              <option value="">-- Select --</option>
              {vehicleTypes.map((type) => (
                <option key={type.id} value={type.name}>{type.name}</option>
              ))}
            </select>
            {errors.vehicle_type && <p className="text-red-500 text-sm mt-1">{errors.vehicle_type[0]}</p>}
          </div>

          {/* Branch */}
          {showBranches && (
            <div>
              <label className="block text-sm">Branch</label>
              <select
                name="branch"
                value={form.branch?.name || ''}
                onChange={(e) => updateBranch(e.target.selectedIndex - 1)}
                disabled={form?.id > 0}
                className="w-full border px-2 py-1 rounded disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
              >
                <option value="">-- Select --</option>
                {branches.map((branch) => (
                  <option key={branch.id} value={branch.name}>{branch.name}</option>
                ))}
              </select>
              {errors.branch && <p className="text-red-500 text-sm mt-1">{errors.branch[0]}</p>}
              {errors.branch_id && <p className="text-red-500 text-sm mt-1">{errors.branch_id[0]}</p>}
            </div>
          )}
        </div>
      </fieldset>

      {/* Section: Documents */}
      {ownVehicle && (
        <fieldset className="border border-gray-300 rounded p-4">
          <legend className="text-lg font-semibold">Documents</legend>

          <div className="grid md:grid-cols-2 gap-4 mt-2">
            <div>
              <label className="block text-sm">Engine Number</label>
              <input name="rc_number" value={form.rc_number} onChange={handleChange} className="w-full border px-2 py-1 rounded" />
              {errors.rc_number && <p className="text-red-500 text-sm mt-1">{errors.rc_number[0]}</p>}
              {renderFileInput('rc', '', form.rc_doc)}
            </div>
            <div>
              <label className="block text-sm">Chassis Number</label>
              <input name="chassis_number" value={form.chassis_number} onChange={handleChange} className="w-full border px-2 py-1 rounded" />
              {errors.chassis_number && <p className="text-red-500 text-sm mt-1">{errors.chassis_number[0]}</p>}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm">Insurance Number</label>
              <input name="insurance_number" value={form.insurance_number} onChange={handleChange} className="w-full border px-2 py-1 rounded" />
              {errors.insurance_number && <p className="text-red-500 text-sm mt-1">{errors.insurance_number[0]}</p>}
              {renderFileInput('insurance', '', form.insurance_doc)}
            </div>
            <div>
              <label className="block text-sm">Insurance Expiry</label>
              <input type="date" name="insurance_expiry" value={form.insurance_expiry} onChange={handleChange} className="w-full border px-2 py-1 rounded" />
              {errors.insurance_expiry && <p className="text-red-500 text-sm mt-1">{errors.insurance_expiry[0]}</p>}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm">FC Date</label>
              <input type="date" name="fc_date" value={form.fc_date} onChange={handleChange} className="w-full border px-2 py-1 rounded" />
              {errors.fc_date && <p className="text-red-500 text-sm mt-1">{errors.fc_date[0]}</p>}
              {renderFileInput('fc', '', form.fc_doc)}
            </div>
          </div>

          <h3 className="text-md font-semibold mt-6 mb-2 uppercase">Other Documents</h3>
          <div className="grid md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm">National Permit Expiry</label>
              <input type="date" name="national_permit_date" value={form.national_permit_date} onChange={handleChange} className="w-full border px-2 py-1 rounded" />
              {errors.national_permit_date && <p className="text-red-500 text-sm mt-1">{errors.national_permit_date[0]}</p>}
              {renderFileInput('national_permit', '', form.national_permit_doc)}
            </div>
            <div>
              <label className="block text-sm">State Permit Expiry</label>
              <input type="date" name="state_permit_date" value={form.state_permit_date} onChange={handleChange} className="w-full border px-2 py-1 rounded" />
              {errors.state_permit_date && <p className="text-red-500 text-sm mt-1">{errors.state_permit_date[0]}</p>}
              {renderFileInput('state_permit', '', form.state_permit_doc)}
            </div>
            <div>
              <label className="block text-sm">Pollution Cert Expiry</label>
              <input type="date" name="pollution_cert_date" value={form.pollution_cert_date} onChange={handleChange} className="w-full border px-2 py-1 rounded" />
              {errors.pollution_cert_date && <p className="text-red-500 text-sm mt-1">{errors.pollution_cert_date[0]}</p>}
              {renderFileInput('pollution_cert', '', form.pollution_cert_doc)}
            </div>
            <div>
              <label className="block text-sm">Tax Receipt Expiry</label>
              <input type="date" name="tax_receipt_date" value={form.tax_receipt_date} onChange={handleChange} className="w-full border px-2 py-1 rounded" />
              {errors.tax_receipt_date && <p className="text-red-500 text-sm mt-1">{errors.tax_receipt_date[0]}</p>}
              {renderFileInput('tax_receipt', '', form.tax_receipt_doc)}
            </div>
          </div>
        </fieldset>
      )}

      <div className="flex justify-end space-x-2">
        <button type="button" onClick={onCancel} className="bg-gray-300 px-4 py-2 rounded">Cancel</button>
        <button type="submit" className="relative bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
          <span className={`${loading ? 'opacity-0' : 'opacity-100'} transition-opacity`}>
            Save
          </span>
          {loading && (
            <span className="absolute inset-0 flex items-center justify-center z-10">
              <Spinner size={18} color="#fff" inline />
            </span>
          )}
        </button>
      </div>
    </form>
  );
};

export default VehicleForm;
